import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowRight, Bike, GraduationCap, Award, Star, Map } from 'lucide-react';
import api from '../api';
import { useApp } from '../contexts/AppContext';
import { ModuleLibrary } from '../components/ModuleLibrary';
import { ModuleViewer } from '../components/ModuleViewer';
import { FormationRequirements } from '../components/FormationRequirements';
import { cn } from '../lib/utils';
import useSeo from '../hooks/useSeo';
import { buildBreadcrumb, combineJsonLd } from '../seo/breadcrumbs';
import { buildCourseSchema, buildItemList } from '../seo/schemas';
import RelatedLinks from '../components/RelatedLinks';

// Formations visibles selon le profil.
// Élèves (rôle eleve ou non connecté) : parcours permis uniquement.
// Enseignants / admin : tout, y compris CCS 2 roues et 20 études de cas CCS.
const ELEVE_FORMATIONS = [
  'AM / BSR',
  'A1',
  'A2',
  'Passerelle A2 vers A',
  'Formation 125 (B→A1)',
  'L5e / Ryker 600-900',
];
const TEACHER_EXTRA = ['CCS 2 roues', 'Études de cas CCS'];

// Métadonnées éditoriales par formation : accent coloré, picto, kicker, image
const FORMATION_META = {
  'AM / BSR': {
    accent: '#16a34a',
    icon: GraduationCap,
    kicker: 'Dès 14 ans · Cyclomoteur',
    short: 'AM · BSR',
    age: '14 ans',
  },
  A1: {
    accent: '#1d4ed8',
    icon: Bike,
    kicker: 'Dès 16 ans · 125 cm³',
    short: 'A1',
    age: '16 ans',
  },
  A2: {
    accent: '#cf2031',
    icon: Award,
    kicker: 'Dès 18 ans · 35 kW',
    short: 'A2',
    age: '18 ans',
    tag: 'POPULAIRE',
  },
  'Passerelle A2 vers A': {
    accent: '#f59e0b',
    icon: Award,
    kicker: '7h · Après 2 ans en A2',
    short: 'Passerelle',
    age: '20 ans',
  },
  'Formation 125 (B→A1)': {
    accent: '#0891b2',
    icon: Bike,
    kicker: '7h · Permis B + 2 ans',
    short: '125 (B)',
    age: '20 ans',
  },
  'L5e / Ryker 600-900': {
    accent: '#7c3aed',
    icon: Map,
    kicker: 'Tricycle motorisé',
    short: 'L5e · Ryker',
    age: '21 ans',
  },
  'CCS 2 roues': {
    accent: '#0e7490',
    icon: GraduationCap,
    kicker: 'Espace enseignant',
    short: 'CCS',
    age: '—',
  },
  'Études de cas CCS': {
    accent: '#7e22ce',
    icon: GraduationCap,
    kicker: 'Espace enseignant',
    short: 'Études CCS',
    age: '—',
  },
};

const metaFor = (name) =>
  FORMATION_META[name] || {
    accent: '#cf2031',
    icon: Bike,
    kicker: 'Permis 2-roues',
    short: name,
    age: '—',
  };

export default function Formation() {
  const { user } = useApp();
  const isTeacher = user && (user.role === 'enseignant' || user.role === 'admin');
  const FORMATIONS = useMemo(
    () => (isTeacher ? [...ELEVE_FORMATIONS, ...TEACHER_EXTRA] : ELEVE_FORMATIONS),
    [isTeacher],
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const initialFormation = searchParams.get('formation') || 'AM / BSR';
  const initialFilter = searchParams.get('filter') || 'all';

  const safeInitial = FORMATIONS.includes(initialFormation) ? initialFormation : 'AM / BSR';

  // SEO : ciblage long-tail sur les permis moto + Course Schema par formation
  useSeo({
    title: 'Formation Permis Moto 2026 — AM, A1, A2, 125',
    description:
      'Toutes les formations 2-roues : AM/BSR, A1, A2, Passerelle A2→A (7h), formation 125 (permis B). Modules pédagogiques gratuits alignés sur le programme moto 2026.',
    keywords:
      'permis moto, permis A1, permis A2, permis A, passerelle A2 A, formation 125, permis B 125, BSR AM, formation 7h, formation 7 heures, permis L5e, tricycle, école moto, ECSR',
    path: '/formation',
    jsonLd: combineJsonLd(
      buildBreadcrumb([['Formations Permis 2-Roues', '/formation']]),
      buildItemList({
        name: 'Formations permis 2-roues disponibles',
        items: [
          { name: 'Permis AM (BSR)', path: '/formation?formation=AM+%2F+BSR', description: 'Cyclomoteur ≤ 50 cm³, dès 14 ans. 8h de formation pratique.' },
          { name: 'Permis A1', path: '/formation?formation=A1', description: 'Moto 125 cm³ (11 kW max), dès 16 ans.' },
          { name: 'Permis A2', path: '/formation?formation=A2', description: 'Moto jusqu\'à 35 kW (47,5 ch), dès 18 ans.' },
          { name: 'Passerelle A2 → A', path: '/formation?formation=Passerelle+A2+vers+A', description: '7h de formation après 2 ans de permis A2.' },
          { name: 'Formation 125 (B→A1)', path: '/formation?formation=Formation+125+%28B%E2%86%92A1%29', description: '7h pour conduire une 125 avec le permis B.' },
          { name: 'L5e / Ryker 600-900', path: '/formation?formation=L5e+%2F+Ryker+600-900', description: 'Tricycle L5e et Can-Am Ryker.' },
        ],
      }),
      buildCourseSchema({
        name: 'Code Moto 2026 — Préparation complète ETM (Épreuve Théorique Moto)',
        description:
          'Formation en ligne gratuite de préparation au Code Moto 2026 (ETM). Couvre les 9 thèmes officiels du programme : circulation, conducteur, route, autres usagers, réglementation, premiers secours, mécanique, équipements, environnement. 719+ questions officielles corrigées.',
        path: '/formation',
        audience: 'Candidats permis A1, A2, Passerelle, 125 (B)',
        duration: 'PT20H',
        level: 'Beginner',
      }),
    ),
  });

  const [formation, setFormation] = useState(safeInitial);
  const [sidefilter, setSidefilter] = useState(initialFilter);
  const [search, setSearch] = useState('');
  const [modules, setModules] = useState([]);
  const [selected, setSelected] = useState(null);
  const [formationStats, setFormationStats] = useState([]);
  // loading préservé pour usage futur (skeleton, indicateur), pas affiché actuellement
  const [, setLoading] = useState(true);

  useEffect(() => {
    api.getFormations().then(setFormationStats).catch(() => {});
  }, []);

  const loadModules = async () => {
    setLoading(true);
    try {
      const list = await api.getModules({ formation, search, sidefilter });
      setModules(list);
      if (list.length > 0) {
        const stillThere = selected && list.find((m) => m.id === selected.id);
        const picked = stillThere ? list.find((m) => m.id === selected.id) : list[0];
        setSelected(picked);
      } else {
        setSelected(null);
      }
    } catch (e) {
      setModules([]);
      setSelected(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadModules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formation, search, sidefilter]);

  const handleFormationChange = (name) => {
    setFormation(name);
    setSearchParams((params) => {
      const p = new URLSearchParams(params);
      p.set('formation', name);
      p.delete('filter');
      return p;
    });
  };

  const handleSelect = async (m) => {
    try {
      const full = await api.getModule(m.id);
      setSelected(full);
    } catch (e) {
      setSelected(m);
    }
  };

  return (
    <div className="space-y-8 sm:space-y-12" data-testid="formation-page">
      {/* ====================================================================
          HERO ÉDITORIAL — pattern oblique + Playfair XXL
          ==================================================================== */}
      <section
        className="relative overflow-hidden rounded-2xl bg-card hero-content-fade"
        data-testid="formation-hero"
        style={{
          background:
            'radial-gradient(ellipse 70% 60% at 100% 0%, rgba(207, 32, 49, 0.08), transparent 65%), hsl(var(--card))',
        }}
      >
        <div
          className="absolute inset-0 opacity-[0.03] pointer-events-none"
          style={{
            backgroundImage:
              'repeating-linear-gradient(45deg, transparent 0 12px, hsl(var(--foreground)) 12px 13px)',
          }}
        />

        <div className="relative px-6 py-10 sm:px-10 sm:py-14 lg:px-14 lg:py-16">
          <div className="flex items-center gap-2 mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--primary))]" />
            <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.18em] text-[hsl(var(--primary))]">
              Parcours de formation · ECSR 2026
            </span>
          </div>

          <h1
            className="font-extrabold tracking-tight text-foreground max-w-4xl leading-[0.98]"
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: 'clamp(2.2rem, 6vw, 4.5rem)',
            }}
          >
            De la découverte à{' '}
            <span className="text-[hsl(var(--primary))] italic font-medium">
              la maîtrise
            </span>
            , en 3 étapes.
          </h1>

          <p
            className="text-sm sm:text-base lg:text-lg text-foreground/70 mt-5 max-w-2xl leading-relaxed italic"
            style={{ fontFamily: "'Crimson Text', Georgia, serif" }}
          >
            AM, A1, A2, Passerelle A, 125 (B→A1), L5e — toutes les formations
            permis 2 et 3 roues alignés sur le programme moto 2026 officiel.
          </p>

          {/* 3 étapes en mini-cards horizontales */}
          <div className="grid sm:grid-cols-3 gap-3 mt-7">
            {[
              { n: '01', t: 'Choisir', d: 'Selon ton âge et ton permis.' },
              { n: '02', t: 'Pré-requis', d: 'Âge, durée, examen, coût.' },
              { n: '03', t: 'Modules', d: 'Théorie · plateau · circulation.' },
            ].map((s) => (
              <div
                key={s.n}
                className="relative overflow-hidden rounded-xl bg-background/60 backdrop-blur-sm border border-border/40 p-4"
              >
                <span className="absolute top-0 inset-x-0 h-[2px] bg-[hsl(var(--primary))]" />
                <div className="flex items-center gap-2 mb-1">
                  <span
                    className="text-2xl font-black tabular-nums tracking-tight text-[hsl(var(--primary))]"
                    style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                  >
                    {s.n}
                  </span>
                  <span className="text-[9px] font-black uppercase tracking-[0.16em] text-foreground/55">
                    Étape
                  </span>
                </div>
                <div
                  className="font-extrabold text-base tracking-tight text-foreground leading-tight"
                  style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                >
                  {s.t}
                </div>
                <div className="text-xs text-foreground/55 mt-1 leading-relaxed">
                  {s.d}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ====================================================================
          ÉTAPE 1 — Sélecteur formation en cards Apple TV
          ==================================================================== */}
      <section data-testid="formation-parcours">
        <div className="flex items-end justify-between mb-4 sm:mb-5 gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <span
                className="text-2xl font-black tabular-nums tracking-tight text-[hsl(var(--primary))] leading-none"
                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
              >
                01
              </span>
              <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.18em] text-[hsl(var(--primary))]">
                Étape · Choisis ta formation
              </span>
            </div>
            <h2
              className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-foreground"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              {isTeacher
                ? 'Tous les parcours · espace enseignant'
                : 'Tous les parcours permis 2 & 3 roues'}
            </h2>
            <p
              className="text-sm text-foreground/60 mt-2 max-w-2xl italic leading-relaxed"
              style={{ fontFamily: "'Crimson Text', Georgia, serif" }}
            >
              {isTeacher
                ? 'AM/BSR, A1, A2, Passerelle, 125, L5e · plus CCS 2 roues et 20 études de cas.'
                : 'Sélectionne ton permis cible. Les modules s\'adapteront automatiquement.'}
            </p>
          </div>
        </div>

        {/* Grid de cards par formation — accent coloré, picto, kicker */}
        <div
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3"
          data-reveal-stagger=""
          data-reveal=""
        >
          {FORMATIONS.map((name) => {
            const fs = formationStats.find((f) => f.name === name);
            const active = formation === name;
            const meta = metaFor(name);
            const Icon = meta.icon;
            return (
              <button
                key={name}
                onClick={() => handleFormationChange(name)}
                data-testid={`formation-btn-${name.replace(/\s+/g, '-').toLowerCase()}`}
                aria-pressed={active}
                className={cn(
                  'group relative overflow-hidden rounded-xl border bg-card text-left transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary))]',
                  active
                    ? 'scale-[1.02] -translate-y-0.5'
                    : 'hover:scale-[1.02] hover:-translate-y-0.5 active:scale-[0.99]',
                )}
                style={{
                  aspectRatio: '4 / 5',
                  borderColor: active ? meta.accent : 'hsl(var(--border) / 0.5)',
                  boxShadow: active
                    ? `0 12px 28px -10px ${meta.accent}66`
                    : '0 6px 18px -12px rgba(0,0,0,0.4)',
                }}
              >
                {/* Filet d'accent en haut */}
                <span
                  className="absolute top-0 inset-x-0 h-[3px] z-10"
                  style={{ background: meta.accent }}
                />

                {/* Numéro fond éditorial (en bas à droite) */}
                <span
                  aria-hidden="true"
                  className="absolute -right-3 -bottom-4 text-[7rem] leading-none font-black opacity-[0.06] tracking-tighter pointer-events-none"
                  style={{
                    color: meta.accent,
                    fontFamily: "'Playfair Display', Georgia, serif",
                  }}
                >
                  {meta.short.charAt(0)}
                </span>

                {/* Pattern oblique très subtil */}
                <div
                  className="absolute inset-0 opacity-[0.04] pointer-events-none"
                  style={{
                    backgroundImage:
                      'repeating-linear-gradient(45deg, transparent 0 8px, currentColor 8px 9px)',
                  }}
                />

                {/* Tag POPULAIRE si défini */}
                {meta.tag && (
                  <span
                    className="absolute top-3 right-3 inline-flex items-center px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-[0.14em] text-white backdrop-blur-md shadow z-10"
                    style={{ background: `${meta.accent}E6` }}
                  >
                    <Star className="w-2 h-2 fill-current mr-0.5" />
                    {meta.tag}
                  </span>
                )}

                <div className="relative h-full flex flex-col justify-between p-3.5 sm:p-4">
                  {/* Pictogramme en haut */}
                  <div
                    className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
                    style={{
                      background: `${meta.accent}1f`,
                      color: meta.accent,
                    }}
                  >
                    <Icon className="w-5 h-5 sm:w-6 sm:h-6" strokeWidth={2} />
                  </div>

                  {/* Texte bas */}
                  <div className="text-left">
                    <span
                      className="block text-[8px] sm:text-[9px] font-black uppercase tracking-[0.16em] mb-1"
                      style={{ color: meta.accent }}
                    >
                      {meta.kicker}
                    </span>
                    <h3
                      className={cn(
                        'text-sm sm:text-base font-extrabold tracking-tight leading-tight transition-colors',
                        active ? 'text-foreground' : 'text-foreground/90',
                      )}
                      style={{
                        fontFamily: "'Playfair Display', Georgia, serif",
                      }}
                    >
                      {meta.short}
                    </h3>
                    <div className="flex items-center justify-between mt-1.5">
                      <span className="text-[10px] text-foreground/50 font-medium">
                        {(fs && fs.count) || 0} module
                        {((fs && fs.count) || 0) > 1 ? 's' : ''}
                      </span>
                      {active && (
                        <span
                          className="inline-flex items-center justify-center w-5 h-5 rounded-full text-white"
                          style={{ background: meta.accent }}
                          aria-hidden="true"
                        >
                          <ArrowRight className="w-3 h-3" />
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* ====================================================================
          ÉTAPE 2 — Pré-requis officiels
          ==================================================================== */}
      <section data-testid="formation-step-2">
        <div className="flex items-end justify-between mb-4 sm:mb-5 gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <span
                className="text-2xl font-black tabular-nums tracking-tight text-[hsl(var(--primary))] leading-none"
                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
              >
                02
              </span>
              <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.18em] text-[hsl(var(--primary))]">
                Étape · Découvre les pré-requis
              </span>
            </div>
            <h2
              className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-foreground"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              {metaFor(formation).short}
              <span className="text-foreground/40 font-normal"> · pré-requis</span>
            </h2>
            <p
              className="text-sm text-foreground/60 mt-2 max-w-2xl italic leading-relaxed"
              style={{ fontFamily: "'Crimson Text', Georgia, serif" }}
            >
              Âge, pré-requis obligatoires, durée, examen, coût — tout ce qu'il
              faut savoir avant de te lancer.
            </p>
          </div>
        </div>
        <FormationRequirements formationKey={formation} />
      </section>

      {/* ====================================================================
          ÉTAPE 3 — Library + Viewer
          ==================================================================== */}
      <section data-testid="formation-step-3">
        <div className="flex items-end justify-between mb-4 sm:mb-5 gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <span
                className="text-2xl font-black tabular-nums tracking-tight text-[hsl(var(--primary))] leading-none"
                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
              >
                03
              </span>
              <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.18em] text-[hsl(var(--primary))]">
                Étape · Déroule tes modules
              </span>
            </div>
            <h2
              className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-foreground"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              {modules.length} module
              {modules.length > 1 ? 's' : ''}
              <span className="text-foreground/40 font-normal">
                {' '}
                · théorie, plateau, circulation
              </span>
            </h2>
            <p
              className="text-sm text-foreground/60 mt-2 max-w-2xl italic leading-relaxed"
              style={{ fontFamily: "'Crimson Text', Georgia, serif" }}
            >
              Avance à ton rythme, module par module. Le contenu s'adapte à ta
              formation sélectionnée.
            </p>
          </div>
        </div>
        <div className="grid lg:grid-cols-[380px_1fr] gap-5">
          <ModuleLibrary
            modules={modules}
            selectedId={selected?.id}
            onSelect={handleSelect}
            search={search}
            onSearchChange={setSearch}
            sidefilter={sidefilter}
            onSidefilterChange={setSidefilter}
          />
          <ModuleViewer module={selected} onUpdated={loadModules} />
        </div>
      </section>

      {/* MAILLAGE INTERNE — boost SEO + temps de session */}
      <RelatedLinks topic="formation" max={5} />
    </div>
  );
}
